import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Col, Nav, Row, Badge, Spinner, Alert, Modal, Form, Button, ProgressBar } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [activityScore, setActivityScore] = useState(85);
  
  // États pour les modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // États pour les activités
  const [activites, setActivites] = useState([]);
  const [connexions, setConnexions] = useState([]);
  const [loadingActivites, setLoadingActivites] = useState(false);
  
  // États pour les formulaires
  const [editForm, setEditForm] = useState({
    nom_utilisateur: "",
    prenom_utilisateur: "",
    email: ""
  });
  
  const [passwordForm, setPasswordForm] = useState({
    ancien_mdp: "",
    nouveau_mdp: "",
    confirmation: ""
  });
  
  // États pour 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFAPassword, setTwoFAPassword] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (user) {
      setEditForm({
        nom_utilisateur: user.nom_utilisateur || "",
        prenom_utilisateur: user.prenom_utilisateur || "",
        email: user.email || ""
      });
    }
  }, [user]);

  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr && userStr !== "undefined" && userStr !== "null") {
        return JSON.parse(userStr);
      }
    } catch (error) {
      console.error("Erreur parsing utilisateur:", error);
      localStorage.removeItem("user");
    }
    return null;
  };

  const fetchActivites = async () => {
    try {
      setLoadingActivites(true);
      const currentUser = getCurrentUser();
      const userId = currentUser.id_utilisateur || currentUser.id;
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/activites.php?action=historique&user_id=${userId}&limite=20`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      const result = await response.json();
      if (result.success) setActivites(result.data);
    } catch (err) {
      console.error("Erreur chargement activités:", err);
    } finally {
      setLoadingActivites(false);
    }
  };

  const fetchConnexions = async () => {
    try {
      const currentUser = getCurrentUser();
      const userId = currentUser.id_utilisateur || currentUser.id;
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/activites.php?action=connexions&user_id=${userId}&limite=5`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      const result = await response.json();
      if (result.success) setConnexions(result.data);
    } catch (err) {
      console.error("Erreur chargement connexions:", err);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      
      if (!currentUser) {
        navigate('/pages/signin2');
        return;
      }

      const userId = currentUser.id_utilisateur || currentUser.id;
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/utilisateurs.php?action=profil&user_id=${userId}`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      const result = await response.json();
      
      if (result.success) {
        setUser(result.data);
        setStats(result.data.stats);
        if (result.data.two_factor_enabled !== undefined) {
          setTwoFAEnabled(result.data.two_factor_enabled == 1);
        }
        if (result.data.stats) {
          const total = (result.data.stats.factures || 0) + (result.data.stats.devis || 0);
          setActivityScore(Math.min(100, Math.round((total / 50) * 100)));
        }
        await fetchActivites();
        await fetchConnexions();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return "À l'instant";
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    if (diffHour < 24) return `Il y a ${diffHour}h`;
    if (diffDay < 7) return `Il y a ${diffDay}j`;
    return formatDate(dateString);
  };

  const getActivityIcon = (type) => {
    const icons = {
      'facture': 'ri-file-list-line',
      'devis': 'ri-file-copy-line',
      'commande': 'ri-shopping-cart-line',
      'reglement': 'ri-bank-card-line',
      'stock': 'ri-stack-line',
      'inventaire': 'ri-file-list-3-line',
      'bl': 'ri-truck-line',
      'connexion': 'ri-login-circle-line'
    };
    return icons[type] || 'ri-history-line';
  };

  const getActivityColor = (type) => {
    const colors = {
      'facture': '#10b981',
      'devis': '#f59e0b',
      'commande': '#3b82f6',
      'reglement': '#8b5cf6',
      'stock': '#6b7280',
      'inventaire': '#ec4899',
      'bl': '#f97316',
      'connexion': '#3b82f6'
    };
    return colors[type] || '#21ACCD';
  };

  const getActivityLabel = (type) => {
    const labels = {
      'facture': 'Facture',
      'devis': 'Devis',
      'commande': 'Commande',
      'reglement': 'Règlement',
      'stock': 'Stock',
      'inventaire': 'Inventaire',
      'bl': 'BL',
      'connexion': 'Connexion'
    };
    return labels[type] || type;
  };

  const handleUpdateProfile = async () => {
    setSaving(true);
    setFormError("");
    try {
      const currentUser = getCurrentUser();
      const userId = currentUser.id_utilisateur || currentUser.id;
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/update_profil.php?user_id=${userId}`,
        {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(editForm)
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        setShowSuccessAlert(true);
        setSuccessMessage("Profil mis à jour avec succès");
        const updatedUser = { ...currentUser, ...editForm };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        await fetchProfile();
        setShowEditModal(false);
        setTimeout(() => setShowSuccessAlert(false), 3000);
      } else {
        setFormError(result.message);
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.nouveau_mdp !== passwordForm.confirmation) {
      setFormError("Les mots de passe ne correspondent pas");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const currentUser = getCurrentUser();
      const userId = currentUser.id_utilisateur || currentUser.id;
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/change_password.php?user_id=${userId}`,
        {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            ancien_mdp: passwordForm.ancien_mdp,
            nouveau_mdp: passwordForm.nouveau_mdp
          })
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        setShowSuccessAlert(true);
        setSuccessMessage("Mot de passe modifié avec succès");
        setPasswordForm({ ancien_mdp: "", nouveau_mdp: "", confirmation: "" });
        setShowPasswordModal(false);
        setTimeout(() => setShowSuccessAlert(false), 3000);
      } else {
        setFormError(result.message);
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle2FA = async () => {
    setTwoFALoading(true);
    setFormError("");
    try {
      const currentUser = getCurrentUser();
      const userId = currentUser.id_utilisateur || currentUser.id;
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/toggle_2fa.php?user_id=${userId}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            password: twoFAPassword,
            enabled: !twoFAEnabled
          })
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        setTwoFAEnabled(!twoFAEnabled);
        await fetchProfile();
        setShowSuccessAlert(true);
        setSuccessMessage(twoFAEnabled ? "2FA désactivée" : "2FA activée avec succès");
        setShow2FAModal(false);
        setTwoFAPassword("");
        setTimeout(() => setShowSuccessAlert(false), 3000);
      } else {
        setFormError(result.message);
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setTwoFALoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { color: '#dc2626', label: 'Administrateur' },
      gestionnaire: { color: '#f59e0b', label: 'Gestionnaire' },
      commercial: { color: '#3b82f6', label: 'Commercial' },
      magasinier: { color: '#10b981', label: 'Magasinier' }
    };
    const badge = badges[role] || { color: '#6b7280', label: role || 'Utilisateur' };
    return (
      <span className="badge-role" style={{ background: badge.color }}>
        {badge.label}
      </span>
    );
  };

  const getUserInitials = () => {
    if (!user) return '';
    const prenom = user.prenom_utilisateur || user.prenom || '';
    const nom = user.nom_utilisateur || user.nom || '';
    return `${prenom.charAt(0) || ''}${nom.charAt(0) || ''}`.toUpperCase() || 'U';
  };

  const getUserFullName = () => {
    if (!user) return 'Utilisateur';
    const prenom = user.prenom_utilisateur || user.prenom || '';
    const nom = user.nom_utilisateur || user.nom || '';
    return (prenom + ' ' + nom).trim() || 'Utilisateur';
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <h5 className="text-muted">Chargement de votre profil...</h5>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <Alert variant="danger" className="text-center border-0 shadow-lg p-5" style={{ borderRadius: '20px' }}>
              <i className="ri-error-warning-line fs-1 mb-3 d-block text-danger"></i>
              <h4>{error}</h4>
              <Button variant="danger" className="mt-3 px-5 rounded-pill" onClick={() => navigate('/pages/signin2')}>
                Retour à la connexion
              </Button>
            </Alert>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        <div className="container-fluid">
          {/* Alertes de succès modernisées */}
          {showSuccessAlert && (
            <Alert variant="success" className="alert-modern mb-4" onClose={() => setShowSuccessAlert(false)} dismissible>
              <i className="ri-checkbox-circle-line me-2 fs-5"></i>
              {successMessage}
            </Alert>
          )}

          <Row className="g-4">
            {/* Colonne principale */}
            <Col xl>
              {/* En-tête du profil modernisé */}
              <Card className="border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                <Card.Body className="p-4 p-md-5" style={{ background: 'linear-gradient(135deg, #21ACCD10 0%, #1a8ba810 100%)' }}>
                  <div className="d-flex flex-column flex-md-row align-items-center gap-4">
                    <div className="profile-avatar-large">
                      {getUserInitials()}
                    </div>
                    <div className="flex-grow-1 text-center text-md-start">
                      <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-start gap-3 mb-2">
                        <h2 className="fw-bold mb-0" style={{ color: '#1e293b' }}>{getUserFullName()}</h2>
                        {getRoleBadge(user?.role)}
                      </div>
                      <p className="text-muted mb-2">
                        <i className="ri-mail-line me-2" style={{ color: '#21ACCD' }}></i>
                        {user?.email}
                      </p>
                      <p className="text-muted mb-3">
                        <i className="ri-calendar-line me-2" style={{ color: '#21ACCD' }}></i>
                        Membre depuis {formatDate(user?.date_creation)}
                      </p>
                      <Button 
                        variant="primary" 
                        className="rounded-pill px-4 py-2"
                        style={{ background: '#21ACCD', border: 'none' }}
                        onClick={() => setShowEditModal(true)}
                      >
                        <i className="ri-edit-line me-2"></i>
                        Modifier le profil
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Navigation par onglets modernisée */}
              <Nav variant="pills" className="nav-pills-custom mb-4 flex-wrap">
                <Nav.Link 
                  className={activeTab === 'info' ? 'active' : ''} 
                  onClick={() => setActiveTab('info')}
                >
                  <i className="ri-information-line me-2"></i>
                  Informations
                </Nav.Link>
                <Nav.Link 
                  className={activeTab === 'securite' ? 'active' : ''} 
                  onClick={() => setActiveTab('securite')}
                >
                  <i className="ri-lock-line me-2"></i>
                  Sécurité
                </Nav.Link>
                <Nav.Link 
                  className={activeTab === 'activite' ? 'active' : ''} 
                  onClick={() => setActiveTab('activite')}
                >
                  <i className="ri-history-line me-2"></i>
                  Activité
                </Nav.Link>
              </Nav>

              {/* Barre de progression d'activité modernisée */}
              <Card className="border-0 shadow-sm rounded-3 mb-4">
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-semibold mb-0" style={{ color: '#1e293b' }}>Niveau d'activité</h6>
                    <span className="fw-bold" style={{ color: '#21ACCD' }}>{activityScore}%</span>
                  </div>
                  <ProgressBar 
                    now={activityScore} 
                    style={{ background: '#e2e8f0', height: '8px', borderRadius: '4px' }}
                  >
                    <ProgressBar 
                      now={activityScore} 
                      style={{ background: '#21ACCD', borderRadius: '4px' }}
                    />
                  </ProgressBar>
                </Card.Body>
              </Card>

              {/* Contenu des onglets modernisé */}
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body className="p-4">
                  {activeTab === 'info' && (
                    <div>
                      <h5 className="fw-bold mb-4" style={{ color: '#1e293b' }}>
                        <i className="ri-user-line me-2" style={{ color: '#21ACCD' }}></i>
                        Informations personnelles
                      </h5>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="info-card">
                            <small className="text-muted d-block mb-1">Nom</small>
                            <p className="fw-semibold fs-5 mb-0">{user?.nom_utilisateur}</p>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="info-card">
                            <small className="text-muted d-block mb-1">Prénom</small>
                            <p className="fw-semibold fs-5 mb-0">{user?.prenom_utilisateur}</p>
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="info-card">
                            <small className="text-muted d-block mb-1">Email</small>
                            <p className="fw-semibold fs-5 mb-0">{user?.email}</p>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {activeTab === 'securite' && (
                    <div>
                      <h5 className="fw-bold mb-4" style={{ color: '#1e293b' }}>
                        <i className="ri-shield-line me-2" style={{ color: '#21ACCD' }}></i>
                        Sécurité du compte
                      </h5>
                      
                      <div className="info-card d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="fw-semibold mb-1">Mot de passe</h6>
                          <p className="text-muted mb-0">••••••••</p>
                        </div>
                        <Button variant="link" className="text-decoration-none p-0" style={{ color: '#21ACCD' }} onClick={() => setShowPasswordModal(true)}>
                          Changer
                        </Button>
                      </div>

                      <div className="info-card d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="fw-semibold mb-1">Authentification à deux facteurs</h6>
                          <Badge className="rounded-pill px-3 py-1" style={{ background: twoFAEnabled ? '#10b981' : '#6b7280' }}>
                            {twoFAEnabled ? "Activé" : "Désactivé"}
                          </Badge>
                        </div>
                        <Button variant="link" className="text-decoration-none p-0" style={{ color: '#21ACCD' }} onClick={() => setShow2FAModal(true)}>
                          {twoFAEnabled ? "Désactiver" : "Activer"}
                        </Button>
                      </div>

                      <div className="info-card">
                        <h6 className="fw-semibold mb-3">Dernières connexions</h6>
                        {connexions.slice(0, 2).map((conn, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                            <div>
                              <i className="ri-map-pin-line me-2" style={{ color: '#21ACCD' }}></i>
                              <span>{conn.ip || 'IP inconnue'}</span>
                            </div>
                            <small className="text-muted">{getRelativeTime(conn.date_creation)}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'activite' && (
                    <div>
                      <h5 className="fw-bold mb-4" style={{ color: '#1e293b' }}>
                        <i className="ri-history-line me-2" style={{ color: '#21ACCD' }}></i>
                        Historique d'activité
                      </h5>
                      
                      {loadingActivites ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" style={{ color: '#21ACCD' }} />
                          <p className="mt-3 text-muted">Chargement des activités...</p>
                        </div>
                      ) : activites.length > 0 ? (
                        <div className="timeline-custom" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {activites.map((activite, index) => (
                            <div key={index} className="timeline-item">
                              <div className="d-flex gap-3">
                                <div className="activity-icon" style={{ backgroundColor: getActivityColor(activite.type) + '20', color: getActivityColor(activite.type) }}>
                                  <i className={getActivityIcon(activite.type)}></i>
                                </div>
                                <div className="flex-grow-1">
                                  <div className="d-flex flex-wrap justify-content-between align-items-start">
                                    <div>
                                      <p className="fw-semibold mb-1">{activite.description}</p>
                                      <Badge className="rounded-pill px-3 py-1 bg-light text-dark">
                                        {getActivityLabel(activite.type)}
                                      </Badge>
                                    </div>
                                    <small className="text-muted">{getRelativeTime(activite.date_activite)}</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="ri-history-line fs-1 text-muted mb-3"></i>
                          <p className="text-muted">Aucune activité récente</p>
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Colonne latérale modernisée */}
            <Col xl="4" className="d-none d-xl-block">
              <Card className="border-0 shadow-sm rounded-3 mb-4">
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3" style={{ color: '#1e293b' }}>
                    <i className="ri-stack-line me-2" style={{ color: '#21ACCD' }}></i>
                    Statistiques
                  </h5>
                  <div className="d-flex justify-content-around text-center">
                    <div>
                      <h3 className="fw-bold mb-1" style={{ color: '#21ACCD' }}>{stats?.factures || 0}</h3>
                      <small className="text-muted">Factures</small>
                    </div>
                    <div className="vr"></div>
                    <div>
                      <h3 className="fw-bold mb-1" style={{ color: '#21ACCD' }}>{stats?.devis || 0}</h3>
                      <small className="text-muted">Devis</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm rounded-3 mb-4">
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3" style={{ color: '#1e293b' }}>
                    <i className="ri-login-circle-line me-2" style={{ color: '#21ACCD' }}></i>
                    Dernières connexions
                  </h5>
                  {connexions.length > 0 ? (
                    connexions.map((conn, index) => (
                      <div key={index} className="mb-3 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="ri-map-pin-line me-2" style={{ color: '#21ACCD' }}></i>
                            <span className="small">{conn.ip || 'IP inconnue'}</span>
                          </div>
                          <small className="text-muted">{getRelativeTime(conn.date_creation)}</small>
                        </div>
                        {conn.user_agent && (
                          <small className="text-muted d-block mt-1">
                            <i className="ri-computer-line me-1"></i>
                            {conn.user_agent.substring(0, 50)}...
                          </small>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-muted text-center">Aucune connexion récente</p>
                  )}
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3" style={{ color: '#1e293b' }}>
                    <i className="ri-information-line me-2" style={{ color: '#21ACCD' }}></i>
                    Contact
                  </h5>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-3">
                      <i className="ri-mail-line me-2" style={{ color: '#21ACCD' }}></i>
                      <span className="small">{user?.email}</span>
                    </li>
                    <li className="mb-3">
                      <i className="ri-calendar-line me-2" style={{ color: '#21ACCD' }}></i>
                      <span className="small">Membre depuis {formatDate(user?.date_creation)}</span>
                    </li>
                    <li>
                      <i className="ri-time-line me-2" style={{ color: '#21ACCD' }}></i>
                      <span className="small">Dernière connexion: {user?.derniere_connexion ? formatDate(user.derniere_connexion) : 'N/A'}</span>
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
        <Footer />
      </div>

      {/* Modal de modification du profil modernisé */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#21ACCD' }}>
          <Modal.Title className="w-100">
            <div className="text-center">
              <div className="confirm-icon mb-3">
                <i className="ri-edit-line text-white" style={{ fontSize: '3rem' }}></i>
              </div>
              <h3 className="fw-bold text-white">Modifier le profil</h3>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          {formError && <Alert variant="danger" className="rounded-3">{formError}</Alert>}
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Nom</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.nom_utilisateur}
                    onChange={(e) => setEditForm({...editForm, nom_utilisateur: e.target.value})}
                    className="border-2 rounded-3"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Prénom</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.prenom_utilisateur}
                    onChange={(e) => setEditForm({...editForm, prenom_utilisateur: e.target.value})}
                    className="border-2 rounded-3"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="border-2 rounded-3"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} className="rounded-pill px-4">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateProfile} disabled={saving} className="rounded-pill px-5" style={{ background: '#21ACCD', border: 'none' }}>
            {saving ? <Spinner size="sm" className="me-2" /> : <i className="ri-save-line me-2"></i>}
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de changement de mot de passe modernisé */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#21ACCD' }}>
          <Modal.Title className="w-100">
            <div className="text-center">
              <div className="confirm-icon mb-3">
                <i className="ri-lock-password-line text-white" style={{ fontSize: '3rem' }}></i>
              </div>
              <h3 className="fw-bold text-white">Changer le mot de passe</h3>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          {formError && <Alert variant="danger" className="rounded-3">{formError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Ancien mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.ancien_mdp}
                onChange={(e) => setPasswordForm({...passwordForm, ancien_mdp: e.target.value})}
                className="border-2 rounded-3"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.nouveau_mdp}
                onChange={(e) => setPasswordForm({...passwordForm, nouveau_mdp: e.target.value})}
                className="border-2 rounded-3"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Confirmer</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.confirmation}
                onChange={(e) => setPasswordForm({...passwordForm, confirmation: e.target.value})}
                className="border-2 rounded-3"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
          <Button variant="outline-secondary" onClick={() => setShowPasswordModal(false)} className="rounded-pill px-4">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleChangePassword} disabled={saving} className="rounded-pill px-5" style={{ background: '#21ACCD', border: 'none' }}>
            {saving ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-line me-2"></i>}
            {saving ? "Changement..." : "Changer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal 2FA modernisé */}
      <Modal show={show2FAModal} onHide={() => {setShow2FAModal(false); setTwoFAPassword(""); setFormError("");}} centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#21ACCD' }}>
          <Modal.Title className="w-100">
            <div className="text-center">
              <div className="confirm-icon mb-3">
                <i className="ri-shield-keyhole-line text-white" style={{ fontSize: '3rem' }}></i>
              </div>
              <h3 className="fw-bold text-white">{twoFAEnabled ? "Désactiver la 2FA" : "Activer la 2FA"}</h3>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          {formError && <Alert variant="danger" className="rounded-3">{formError}</Alert>}
          <div className="text-center py-3">
            <div className="display-1 mb-4" style={{ color: '#21ACCD' }}>
              <i className="ri-shield-line"></i>
            </div>
            <h5 className="mb-3">
              {twoFAEnabled ? "Pour désactiver la 2FA" : "Pour activer la 2FA"}
            </h5>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Confirmez votre mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={twoFAPassword}
                onChange={(e) => setTwoFAPassword(e.target.value)}
                placeholder="Votre mot de passe"
                size="lg"
                className="border-2 rounded-3"
              />
            </Form.Group>
            <Alert variant="info" className="rounded-3 text-start">
              <i className="ri-information-line me-2"></i>
              L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire.
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
          <Button variant="outline-secondary" onClick={() => {setShow2FAModal(false); setTwoFAPassword("");}} className="rounded-pill px-4">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleToggle2FA} disabled={twoFALoading || !twoFAPassword} className="rounded-pill px-5" style={{ background: '#21ACCD', border: 'none' }}>
            {twoFALoading ? <Spinner size="sm" className="me-2" /> : <i className="ri-shield-check-line me-2"></i>}
            {twoFALoading ? "Chargement..." : (twoFAEnabled ? "Désactiver" : "Activer")}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .profile-avatar-large {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: linear-gradient(135deg, #21ACCD, #1a8ba8);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          font-weight: bold;
          border: 4px solid white;
          box-shadow: 0 10px 30px rgba(33, 172, 205, 0.3);
          transition: all 0.3s ease;
        }

        .profile-avatar-large:hover {
          transform: scale(1.05);
        }

        .badge-role {
          display: inline-block;
          padding: 0.25rem 1rem;
          border-radius: 50px;
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .nav-pills-custom .nav-link {
          border: none;
          padding: 10px 20px;
          font-weight: 600;
          color: #64748b;
          transition: all 0.3s ease;
          border-radius: 12px;
          margin: 0 5px;
          cursor: pointer;
        }

        .nav-pills-custom .nav-link:hover {
          color: #21ACCD;
          background: rgba(33, 172, 205, 0.1);
        }

        .nav-pills-custom .nav-link.active {
          background: #21ACCD;
          color: white !important;
        }

        .info-card {
          background: #f8fafc;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
          transition: all 0.3s ease;
        }

        .info-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }

        .timeline-custom {
          position: relative;
          padding-left: 0;
        }

        .timeline-item {
          position: relative;
          padding-bottom: 1.25rem;
          margin-bottom: 0.5rem;
          border-left: 2px solid #e2e8f0;
          padding-left: 1.5rem;
        }

        .timeline-item:last-child {
          border-left-color: transparent;
        }

        .timeline-item::before {
          content: '';
          position: absolute;
          left: -6px;
          top: 0;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #21ACCD;
          border: 2px solid white;
        }

        .activity-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }

        .alert-modern {
          border: none;
          border-radius: 12px;
          padding: 1rem 1.5rem;
        }

        .modern-modal .modal-content {
          border-radius: 20px;
          border: none;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          overflow: hidden;
        }

        .confirm-icon {
          animation: scaleIn 0.5s ease-out;
        }

        @keyframes scaleIn {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }

        .border-2 {
          border-width: 2px !important;
          border-color: #e2e8f0;
        }

        .border-2:focus {
          border-color: #21ACCD;
          box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
        }

        .rounded-3 {
          border-radius: 12px !important;
        }

        .rounded-pill {
          border-radius: 50px !important;
        }
      `}</style>
    </>
  );
}